/**
 * QuickScanStep3Appliances — "Welke grote verbruikers heeft u?"
 * Multi-select with "geen" / "weet ik niet" exclusive options.
 * No required field — empty means none/unknown.
 *
 * Jun 2026 commercial tuning: prominent "Toon mijn demo direct" skip CTA
 * at the top, because POST_TUNING_T0 baseline showed 5/5 sessions
 * abandoned on step 3 (multi-select cognitive load — users don't know
 * the field is optional).
 */
import { Label } from '@/components/ui/label';
import { ArrowRight } from 'lucide-react';

const OPTIONS = [
  { value: 'heat_pump', label: 'Warmtepomp' },
  { value: 'ev_charger', label: 'EV-lader / Laadpaal' },
  { value: 'airco', label: 'Airco' },
  { value: 'electric_boiler', label: 'Elektrische boiler' },
  { value: 'induction_cooking', label: 'Inductie koken' },
  { value: 'none', label: 'Geen van deze' },
  { value: 'unknown', label: 'Weet ik niet' },
];

export default function QuickScanStep3Appliances({ value, onToggle, onSkipToDemo }) {
  const selected = value || [];

  return (
    <div data-testid="qs-step-3" className="space-y-5">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-medium text-white leading-tight"
          style={{ fontFamily: 'Outfit' }}
          data-testid="qs-step-3-headline"
        >
          Welke grote verbruikers heeft u thuis?
        </h2>
        <p className="text-sm text-white/65 mt-1.5">
          Meerdere kan. Niets kiezen mag ook — u kunt direct door naar uw demo.
        </p>
      </div>

      {/* Jun 2026 — prominent fast-path for users who don't want the multi-select. */}
      {onSkipToDemo && (
        <button
          type="button"
          onClick={onSkipToDemo}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 text-sm font-medium transition-colors"
          data-testid="qs-step-3-skip-to-demo-btn"
        >
          Toon mijn demo direct
          <ArrowRight className="w-4 h-4" />
        </button>
      )}

      <div>
        <Label className="sr-only">Grote verbruikers</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OPTIONS.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                aria-pressed={active}
                className={`text-left px-4 py-3.5 rounded-xl border transition-all ${
                  active
                    ? 'border-emerald-400/60 bg-emerald-500/15 ring-1 ring-emerald-400/30'
                    : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-emerald-400/30'
                }`}
                data-testid={`qs-appliance-${opt.value}`}
              >
                <div className="flex items-start gap-2.5">
                  <div
                    className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      active ? 'border-emerald-400 bg-emerald-400' : 'border-white/30'
                    }`}
                  >
                    {active && (
                      <svg viewBox="0 0 12 12" className="w-3 h-3 text-emerald-950" fill="currentColor" aria-hidden="true">
                        <path d="M10 3.5L4.7 8.8 2 6.1l.8-.8L4.7 7.2 9.2 2.7z" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium text-white">{opt.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
